/**
 * Relecture du plafond servi après un ajout réussi (DEV-2254).
 *
 * Module sans dépendance statique : les composants d'ajout l'importent sans
 * tirer le store ni les services au chargement (leurs tests les simulent).
 */
import { shallowRef } from "vue"

/**
 * Dernière réponse de `GET /company/billing/source/count` lue ici, numérotée :
 * la carte d'abonnement (`useCompanyPlafond`) la reprend au lieu de relancer
 * le même appel quand le plafond du store change (un seul appel de comptage
 * par ajout, revue de #806).
 */
export const dernierComptage = shallowRef<{
    reponse: unknown
    numero: number
    /** Entreprise pour laquelle la réponse a été lue : jamais appliquée à une autre */
    companyId: unknown
} | null>(null)
let numero = 0

/**
 * Relit `GET /company/billing/source/count` et pose son bloc `plafond` dans le
 * store, seulement si l'entreprise du store n'a pas changé pendant la requête
 * (changement d'espace : le bloc de l'ancienne entreprise ne doit pas écraser
 * celui de la nouvelle). Ne lève jamais : un échec garde le plafond connu. Une réponse sans clé
 * `plafond` (API qui ne le sert pas encore) ne change rien.
 */
export async function rafraichirPlafondServi(): Promise<void> {
    try {
        const [{ useAuthStore }, { CompanyService }] = await Promise.all([
            import("@core/stores/auth.store"),
            import("@core/services/company.service"),
        ])
        const store = useAuthStore()
        const entrepriseAvant = store.companyId
        const reponse = await new CompanyService().getBillingSourceCount()
        if (store.companyId !== entrepriseAvant) return
        // posée avant le store : la carte la lit dans son `watch`
        dernierComptage.value = {
            reponse,
            numero: ++numero,
            companyId: entrepriseAvant,
        }
        if (reponse && typeof reponse === "object" && "plafond" in reponse) {
            store.setCompanyPlafond(reponse.plafond)
        }
    } catch {
        // Lecture d'appoint : l'ajout a réussi, le plafond connu reste affiché
    }
}
